import styled from "@emotion/styled";
import { motion } from "framer-motion";
import type { TourPlacement } from "./types";
import {
  TOUR_Z_BACKDROP,
  TOUR_Z_HIGHLIGHT,
  TOUR_Z_TOOLTIP,
  HIGHLIGHT_RING_INSET_PX,
  HIGHLIGHT_RING_RADIUS_OFFSET_PX,
  HIGHLIGHT_RING_DEFAULT_RADIUS,
  OUTER_BACKDROP_MAX_SIZE_PX,
  TOOLTIP_OFFSET_PX,
  TOOLTIP_MAX_WIDTH_PX,
  TOOLTIP_MIN_WIDTH_PX,
  VIEWPORT_SAFE_MARGIN_PX,
  ARROW_WIDTH,
  ARROW_HEIGHT,
  ARROW_GAP_PX,
  ARROW_SIDE_GAP_PX,
  toAnchorVar
} from "./constants";

const DOUBLE_MARGIN = VIEWPORT_SAFE_MARGIN_PX * 2;

const customProps = new Set(["anchorName", "placement", "borderRadius"]);
const shouldForwardProp = (prop: string): boolean => !customProps.has(prop);

// ── Backdrop ────────────────────────────────────────────

export const Backdrop = styled(motion.div)`
  label: TourGuideBackdrop;
  position: fixed;
  inset: 0;
  background: transparent;
  z-index: ${TOUR_Z_BACKDROP};
`;

// ── Highlight ring ──────────────────────────────────────

export const HighlightRing = styled(motion.div, { shouldForwardProp })<{
  anchorName: string;
  borderRadius?: string;
}>`
  label: TourGuideHighlightRing;
  position: fixed;
  position-anchor: ${({ anchorName }) => toAnchorVar(anchorName)};
  top: anchor(top);
  left: anchor(left);
  right: anchor(right);
  bottom: anchor(bottom);
  margin: ${HIGHLIGHT_RING_INSET_PX}px;
  border-radius: ${({ borderRadius }) =>
    borderRadius
      ? `calc(${borderRadius} + ${HIGHLIGHT_RING_RADIUS_OFFSET_PX}px)`
      : HIGHLIGHT_RING_DEFAULT_RADIUS};
  border: 2px solid var(--color-highlight-blue, #3897f0);
  --outer-backdrop-size: min(${OUTER_BACKDROP_MAX_SIZE_PX}px, 100vw);
  box-shadow:
    0 0 12px rgba(56, 151, 240, 0.35),
    0 0 var(--outer-backdrop-size) var(--outer-backdrop-size)
      rgba(56, 151, 240, 0.2);
  z-index: ${TOUR_Z_HIGHLIGHT};
  cursor: pointer;
`;

// ── Tooltip container ───────────────────────────────────

const horizontalClamp = `
  width: max-content;
  max-width: min(${TOOLTIP_MAX_WIDTH_PX}px, calc(100vw - ${DOUBLE_MARGIN}px));
`;

const placementStyles: Record<TourPlacement, string> = {
  top: `
    bottom: calc(anchor(top) + ${TOOLTIP_OFFSET_PX}px);
    ${horizontalClamp}
  `,
  bottom: `
    top: calc(anchor(bottom) + ${TOOLTIP_OFFSET_PX}px);
    ${horizontalClamp}
  `,
  left: `
    right: calc(anchor(left) + ${TOOLTIP_OFFSET_PX}px);
    top: anchor(center);
    translate: 0 -50%;
  `,
  right: `
    left: calc(anchor(right) + ${TOOLTIP_OFFSET_PX}px);
    top: anchor(center);
    translate: 0 -50%;
  `
};

export const TooltipContainer = styled(motion.div, { shouldForwardProp })<{
  anchorName: string;
  placement: TourPlacement;
}>`
  label: TourGuideTooltip;
  position: fixed;
  position-anchor: ${({ anchorName }) => toAnchorVar(anchorName)};
  z-index: ${TOUR_Z_TOOLTIP};
  ${({ placement }) => placementStyles[placement]}
`;

// ── Tooltip inner elements ──────────────────────────────

export const TooltipCard = styled.div`
  label: TourGuideTooltipCard;
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
  background: var(--color-charcoal, #212121);
  color: var(--color-white, #fff);
  border-radius: 8px;
  padding: 12px 14px 12px;
  min-width: ${TOOLTIP_MIN_WIDTH_PX}px;
  max-width: 280px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  outline: none;

  &:active {
    opacity: 0.92;
  }
`;

export const TooltipText = styled.p`
  label: TourGuideText;
  font-family: var(--font-family);
  font-size: 14px;
  font-weight: 400;
  line-height: 1.4;
  letter-spacing: -0.28px;
  font-feature-settings:
    "liga" off,
    "clig" off;
  margin: 0 0 12px;
  padding: 0 4px 0 0;
`;

export const TooltipFooter = styled.div`
  label: TourGuideFooter;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

export const TooltipStepCounter = styled.span`
  label: TourGuideStepCounter;
  font-family: var(--font-family);
  font-size: 12px;
  line-height: 1.4;
  letter-spacing: -0.28px;
`;

export const TooltipButton = styled.span`
  label: TourGuideButton;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 28px;
  padding: 0 12px;
  background: transparent;
  color: var(--color-white, #fff);
  border: 1px solid var(--color-white, #fff);
  border-radius: 200px;
  font-family: var(--font-family);
  font-size: 14px;
  font-weight: 400;
  pointer-events: none;
`;

// ── Arrow ───────────────────────────────────────────────

const arrowPlacementStyles: Record<TourPlacement, string> = {
  bottom: `
    top: calc(anchor(bottom) + ${ARROW_GAP_PX}px);
    left: calc(anchor(center) - ${ARROW_WIDTH / 2}px);
  `,
  top: `
    bottom: calc(anchor(top) + ${ARROW_GAP_PX}px);
    left: calc(anchor(center) - ${ARROW_WIDTH / 2}px);
  `,
  left: `
    right: calc(anchor(left) + ${ARROW_SIDE_GAP_PX}px);
    top: calc(anchor(center) - ${ARROW_HEIGHT / 2}px);
  `,
  right: `
    left: calc(anchor(right) + ${ARROW_SIDE_GAP_PX}px);
    top: calc(anchor(center) - ${ARROW_HEIGHT / 2}px);
  `
};

export const TooltipArrow = styled(motion.div, { shouldForwardProp })<{
  anchorName: string;
  placement: TourPlacement;
}>`
  label: TourGuideArrow;
  position: fixed;
  position-anchor: ${({ anchorName }) => toAnchorVar(anchorName)};
  z-index: ${TOUR_Z_TOOLTIP};
  width: ${ARROW_WIDTH}px;
  height: ${ARROW_HEIGHT}px;
  color: var(--color-charcoal, #212121);
  pointer-events: none;
  ${({ placement }) => arrowPlacementStyles[placement]}

  svg {
    position: absolute;
    inset: 0;
    display: block;
  }
`;
